import type { ReactElement } from 'react';
import type { PoStatusFilter, PurchaseOrder } from '../../domain/types';
import { formatMoney, formatPoStatus, purchaseOrderGrandTotal } from '../../domain/formatters';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

const PO_COLUMNS: GroupedColumnDef[] = [
  { id: 'poNumber', label: 'PO #', header: 'PO #' },
  { id: 'requestNumber', label: 'Req #', header: 'Req #' },
  { id: 'well', label: 'Well', header: 'Well' },
  { id: 'vendor', label: 'Supplier', header: 'Supplier' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'total', label: 'Total', header: 'Total' },
  { id: 'created', label: 'Created', header: 'Created' }
];

function poGroupValue(po: PurchaseOrder, columnId: string): string {
  switch (columnId) {
    case 'poNumber':
      return po.purchaseOrderNumber;
    case 'requestNumber':
      return po.requestNumber;
    case 'well': {
      const w = po.wellInfo;
      const parts = [w.wellName?.trim(), w.field?.trim()].filter(Boolean);
      return parts.length > 0 ? parts.join(' · ') : '—';
    }
    case 'vendor':
      return po.vendorName;
    case 'status':
      return formatPoStatus(po.status);
    case 'total':
      return formatMoney(purchaseOrderGrandTotal(po));
    case 'created':
      return new Date(po.createdAtUtc).toLocaleString();
    default:
      return '—';
  }
}

interface PurchaseOrdersGridProps {
  rows: PurchaseOrder[];
  selectedPoId: string | null;
  onSelectPo: (id: string) => void;
  onOpenPoInPage?: (id: string) => void;
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  statusFilter: PoStatusFilter;
  onStatusFilterChange: (filter: PoStatusFilter) => void;
}

export function PurchaseOrdersGrid({
  rows,
  selectedPoId,
  onSelectPo,
  onOpenPoInPage,
  loading,
  page: currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  statusFilter,
  onStatusFilterChange
}: PurchaseOrdersGridProps): ReactElement {
  function renderPoRow(po: PurchaseOrder): ReactElement {
    return (
      <tr
        className={selectedPoId === po.id ? 'selected' : undefined}
        onClick={() => onSelectPo(po.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onOpenPoInPage?.(po.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectPo(po.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td>{po.purchaseOrderNumber}</td>
        <td>{po.requestNumber}</td>
        <td>
          {po.wellInfo.wellName?.trim() || '—'}
          {po.wellInfo.field?.trim() ? (
            <span className="po-well-field"> · {po.wellInfo.field.trim()}</span>
          ) : null}
        </td>
        <td>{po.vendorName}</td>
        <td>
          <span className={`status-pill po-status-${po.status}`}>{formatPoStatus(po.status)}</span>
        </td>
        <td>{formatMoney(purchaseOrderGrandTotal(po))}</td>
        <td>{new Date(po.createdAtUtc).toLocaleString()}</td>
      </tr>
    );
  }

  return (
    <section className="card requests-grid-card">
      <h2>Purchase orders</h2>
      <p className="hint">
        Click a row for the side panel; double-click to open the full page (PO #, vendor, lines, payment status).
      </p>
      <div className="row filter-row">
        <label htmlFor="poStatusFilter">Status</label>
        <select
          id="poStatusFilter"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as PoStatusFilter)}
        >
          <option value="all">All</option>
          <option value="created">Unpaid (created)</option>
          <option value="paid">Paid</option>
        </select>
      </div>
      {loading && <p className="hint">Loading purchase orders…</p>}
      <GroupedDataGrid<PurchaseOrder>
        columns={PO_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={poGroupValue}
        renderRow={renderPoRow}
        colSpan={7}
        emptyMessage="No purchase orders yet."
        groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group. Reorder chips for nested groups. Applies to this page."
      />
      <GridPaginationBar
        idPrefix="po-grid"
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
